import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type {
  TaskTrackerBoardColumn,
  TaskTrackerBoardLaneItem,
} from '../domain.types'
import {
  appendCardsToBoardItemColumn,
  applyBoardRefreshItemsToBoardItems,
  extractColumnsFromBoardItems,
  mergeColumnsIntoBoardItems,
  removeCardFromBoardItems,
  replaceBoardItemColumnCards,
  syncUpdatedCardInBoardItems,
} from '../../lib/taskTrackerBoardItems.utils'
import { removeBoard } from '../shared/boardCleanup.actions'
import {
  loadMoreColumnItemsThunk,
  reloadColumnItemsThunk,
} from '../columnPagination/columnPagination.thunks'
import { syncDrawerTaskThunk } from '../taskDrawer/taskDrawer.thunks'
import {
  createEmptyBoardKanbanBoardState,
  createInitialBoardKanbanState,
} from './boardKanban.state.utils'
import { fetchBoardKanbanThunk } from './boardKanban.thunks'
import { refreshBoardItemsThunk } from './boardKanban.refresh.thunks'

const ensureBoard = (
  state: ReturnType<typeof createInitialBoardKanbanState>,
  boardId: number,
) => {
  if (!state.byBoardId[boardId]) {
    state.byBoardId[boardId] = createEmptyBoardKanbanBoardState()
  }
  return state.byBoardId[boardId]
}

const syncColumnsFromBoardItems = (
  boardItems: TaskTrackerBoardLaneItem[],
): TaskTrackerBoardColumn[] => extractColumnsFromBoardItems(boardItems)

const boardKanbanSlice = createSlice({
  name: 'taskTrackerBoardKanban',
  initialState: createInitialBoardKanbanState(),
  reducers: {
    bumpBoardReloadToken(state, action: PayloadAction<number>) {
      const boardId = action.payload
      state.reloadTokens[boardId] = (state.reloadTokens[boardId] ?? 0) + 1
    },
    clearBoardKanban(state, action: PayloadAction<number | null>) {
      if (action.payload == null) return
      state.byBoardId[action.payload] = createEmptyBoardKanbanBoardState()
    },
    setBoardItems(
      state,
      action: PayloadAction<{
        boardId: number
        boardItems:
          | TaskTrackerBoardLaneItem[]
          | ((prev: TaskTrackerBoardLaneItem[]) => TaskTrackerBoardLaneItem[])
      }>,
    ) {
      const boardState = ensureBoard(state, action.payload.boardId)
      const next =
        typeof action.payload.boardItems === 'function'
          ? action.payload.boardItems(boardState.boardItems)
          : action.payload.boardItems
      boardState.boardItems = next
      boardState.columns = syncColumnsFromBoardItems(next)
    },
    setBoardColumns(
      state,
      action: PayloadAction<{
        boardId: number
        columns:
          | TaskTrackerBoardColumn[]
          | ((prev: TaskTrackerBoardColumn[]) => TaskTrackerBoardColumn[])
      }>,
    ) {
      const boardState = ensureBoard(state, action.payload.boardId)
      const nextColumns =
        typeof action.payload.columns === 'function'
          ? action.payload.columns(boardState.columns)
          : action.payload.columns
      const nextBoardItems = mergeColumnsIntoBoardItems(
        boardState.boardItems,
        nextColumns,
      )
      boardState.boardItems = nextBoardItems
      boardState.columns = syncColumnsFromBoardItems(nextBoardItems)
    },
    removeBoardCard(
      state,
      action: PayloadAction<{ boardId: number; taskId: number }>,
    ) {
      const boardState = ensureBoard(state, action.payload.boardId)
      boardState.boardItems = removeCardFromBoardItems(
        boardState.boardItems,
        action.payload.taskId,
      )
      boardState.columns = syncColumnsFromBoardItems(boardState.boardItems)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeBoard, (state, action) => {
        const boardId = action.payload.boardId
        delete state.reloadTokens[boardId]
        delete state.byBoardId[boardId]
      })
      .addCase(fetchBoardKanbanThunk.pending, (state, action) => {
        const boardState = ensureBoard(state, action.meta.arg.boardId)
        boardState.pendingFetches = (boardState.pendingFetches ?? 0) + 1
        if (action.meta.arg.isInitialLoad) {
          boardState.loading = true
        } else {
          boardState.refreshing = true
        }
        boardState.error = null
      })
      .addCase(fetchBoardKanbanThunk.fulfilled, (state, action) => {
        const { boardId, details } = action.payload
        const boardState = ensureBoard(state, boardId)
        boardState.board = details.board
        boardState.boardItems = details.boardItems
        boardState.columns = details.columns
        boardState.stickers = details.stickers
        boardState.cardsHasMore = details.cardsHasMore
        boardState.cardsTotalCount = details.cardsTotalCount
        boardState.error = null
        boardState.pendingFetches = Math.max(0, (boardState.pendingFetches ?? 0) - 1)
        if (boardState.pendingFetches === 0) {
          boardState.loading = false
          boardState.refreshing = false
        }
      })
      .addCase(fetchBoardKanbanThunk.rejected, (state, action) => {
        const payload = action.payload as {
          boardId: number
          isInitialLoad: boolean
          error: string
        }
        const boardState = ensureBoard(state, payload.boardId)
        boardState.board = null
        boardState.boardItems = []
        boardState.columns = []
        boardState.stickers = []
        boardState.cardsHasMore = false
        boardState.cardsTotalCount = 0
        boardState.error = payload.error
        boardState.pendingFetches = Math.max(0, (boardState.pendingFetches ?? 0) - 1)
        if (boardState.pendingFetches === 0) {
          boardState.loading = false
          boardState.refreshing = false
        }
      })
      .addCase(loadMoreColumnItemsThunk.fulfilled, (state, action) => {
        const { boardId, columnId, page } = action.payload
        const boardState = ensureBoard(state, boardId)
        boardState.boardItems = appendCardsToBoardItemColumn(
          boardState.boardItems,
          columnId,
          page.items,
          page.totalCount,
        )
        boardState.columns = syncColumnsFromBoardItems(boardState.boardItems)
      })
      .addCase(reloadColumnItemsThunk.fulfilled, (state, action) => {
        const { boardId, columnId, page } = action.payload
        const boardState = ensureBoard(state, boardId)
        boardState.boardItems = replaceBoardItemColumnCards(
          boardState.boardItems,
          columnId,
          page.items,
          page.totalCount,
        )
        boardState.columns = syncColumnsFromBoardItems(boardState.boardItems)
      })
      .addCase(refreshBoardItemsThunk.fulfilled, (state, action) => {
        const { boardId, result } = action.payload
        const boardState = state.byBoardId[boardId]
        if (!boardState) return

        boardState.boardItems = applyBoardRefreshItemsToBoardItems(
          boardState.boardItems,
          result,
        )
        boardState.columns = syncColumnsFromBoardItems(boardState.boardItems)
      })
      .addCase(syncDrawerTaskThunk.fulfilled, (state, action) => {
        const { task } = action.payload
        const boardId = task.boardId
        if (state.byBoardId[boardId]) {
          const nextBoardItems = syncUpdatedCardInBoardItems(
            state.byBoardId[boardId].boardItems,
            task,
          )
          state.byBoardId[boardId].boardItems = nextBoardItems
          state.byBoardId[boardId].columns =
            syncColumnsFromBoardItems(nextBoardItems)
        }
      })
      .addCase(syncDrawerTaskThunk.rejected, (state, action) => {
        const payload = action.payload as {
          taskId: number
          fallbackTask: import('../domain.types').TaskTrackerBoardCard
        }
        const boardId = payload.fallbackTask.boardId
        if (state.byBoardId[boardId]) {
          const nextBoardItems = syncUpdatedCardInBoardItems(
            state.byBoardId[boardId].boardItems,
            payload.fallbackTask,
          )
          state.byBoardId[boardId].boardItems = nextBoardItems
          state.byBoardId[boardId].columns =
            syncColumnsFromBoardItems(nextBoardItems)
        }
      })
  },
})

export const {
  bumpBoardReloadToken,
  clearBoardKanban,
  setBoardItems,
  setBoardColumns,
  removeBoardCard,
} = boardKanbanSlice.actions

export default boardKanbanSlice.reducer
